import React, { useMemo } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import BookProcessor from '../views/BookProcessor'
import Authenticator from '../components/Auth/Authenticator'
import Organization from '../views/Organization'
import { AbilityContext, defineAbilitiesFor } from '../utils/AbilityUtil'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { MenuItemProps } from '../configs/menuItemsConfig'
import { menuItems } from '../configs/menuConfig'
import LayoutWithPanels from '../views/Layout/LayoutWithPanels'
import { engineeringRoles, UserRoleNames } from '../models/AccessControl'
import Subscription from '../views/Subscription'
import { commonToolItems, ToolItemProps } from '../configs/toolItemsConfig'

export const ConstructionRoutes: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user)
  const ability = useMemo(() => defineAbilitiesFor(user), [user])

  const filterRoutesByRole = (
    routes: MenuItemProps[],
    userRole: UserRoleNames,
  ): MenuItemProps[] => {
    return routes
      .filter((route) => !route.roles || route.roles.includes(userRole))
      .map((route) => ({
        ...route,
        nestedRoutes: route.nestedRoutes
          ? filterRoutesByRole(route.nestedRoutes, userRole)
          : undefined,
      }))
  }

  const generateMenuRoutes = (routes: MenuItemProps[]): React.ReactNode[] => {
    const filteredRoutes = filterRoutesByRole(
      routes,
      user ? user?.role.name : UserRoleNames.Viewer,
    )

    return filteredRoutes.flatMap((item: MenuItemProps, index: number) => {
      const Component = item.component
      const nestedRoutes = item.nestedRoutes
        ? generateMenuRoutes(item.nestedRoutes)
        : []

      return [
        <Route key={index} path={item.route} element={<Component />} />,
        ...nestedRoutes,
      ]
    })
  }

  const generateToolRoutes = (
    routes: (ToolItemProps | undefined)[],
  ): React.ReactNode[] => {
    return routes.map((item: ToolItemProps | undefined, index: number) => {
      if (item) {
        const Component = item.component
        const nestedRoutes = item.nestedRoutes
          ? generateToolRoutes(
              item.nestedRoutes.filter((nestedRoute) => nestedRoute.route),
            )
          : []

        return (
          <Route key={index} path={item.route} element={<Component />}>
            {nestedRoutes}
          </Route>
        )
      }
    })
  }

  const defaultPath = !!!user
    ? '/'
    : engineeringRoles.includes(user?.role.name)
      ? '/projects'
      : '/home'

  return (
    <Authenticator>
      <AbilityContext.Provider value={ability}>
        <Routes>
          <Route path='/extractor' element={<BookProcessor />} />
          <Route path='/subscriptions' element={<Subscription />} />
          <Route path='/' element={<LayoutWithPanels />}>
            {generateMenuRoutes(
              menuItems[user ? user.role.name : UserRoleNames.Viewer],
            )}
            {generateToolRoutes([
              commonToolItems.find((tool) => tool.label === 'Settings'),
            ])}
            {/* Default route redirection */}
            <Route path='/' element={<Navigate to={defaultPath} replace />} />
            {/* Catch-all route for undefined paths */}
            <Route path='*' element={<Navigate to={defaultPath} replace />} />
          </Route>
        </Routes>
      </AbilityContext.Provider>
    </Authenticator>
  )
}
